import { useCallback } from "react";
import {
  isNativeSpeechRecognitionSupported,
  stopNativeSpeech,
} from "../lib/voice-browser-runtime";
import {
  VOICE_CONVERSATION_STORAGE_KEY,
  VOICE_REPLY_AUDIO_STORAGE_KEY,
  safeLocalStorageSet,
} from "./voice-assistant-storage";
import {
  buildInfractionPrompt,
  type InfractionPromptInput,
} from "./voice-prompt-builders";
import {
  startVoiceListeningSession,
  stopVoiceRecordingSession,
  type StopRecordingOptions,
} from "./voice-listening-session";
import { processVoiceRecordingFallback } from "./voice-recording-fallback";
import {
  sendVoiceTextRequest,
  type VoiceSendOptions,
  type VoiceSendResult,
} from "./voice-send-text";
import { isVoiceWidgetSandboxPreview } from "./voice-sandbox-mode";
import type { UseVoiceAssistantRuntimeInput } from "./voice-assistant-runtime-types";

export function useVoiceAssistantRuntime({
  backend,
  conversationId,
  permissionLevel,
  permission,
  isSupported,
  isSoundEnabled,
  audioRef,
  enabledRef,
  replyAudioEnabledRef,
  busyRef,
  abortRef,
  micReleaseRef,
  micStreamRef,
  micRequestIdRef,
  errorRecoveryRef,
  recordingRef,
  readySoundPlayedRef,
  startListeningRef,
  onTranscriptRef,
  requestMic,
  setConversationId,
  setRuntimeState,
  setLastUserText,
  setLastAssistantText,
  setHasUserRequestActivity,
  setReplyAudioEnabledState,
}: UseVoiceAssistantRuntimeInput) {
  const setErrorState = useCallback(() => {
    setRuntimeState("error");
    if (errorRecoveryRef.current) {
      clearTimeout(errorRecoveryRef.current);
      errorRecoveryRef.current = null;
    }
  }, [errorRecoveryRef, setRuntimeState]);

  const stopPlayback = useCallback(() => {
    stopNativeSpeech();
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = "";
      audioRef.current = null;
    }
  }, [audioRef]);

  const setReplyAudioEnabled = useCallback(
    (enabled: boolean) => {
      replyAudioEnabledRef.current = enabled;
      setReplyAudioEnabledState(enabled);
      safeLocalStorageSet(
        VOICE_REPLY_AUDIO_STORAGE_KEY,
        enabled ? "true" : "false",
      );
      if (!enabled) stopNativeSpeech();
    },
    [replyAudioEnabledRef, setReplyAudioEnabledState],
  );

  const stopRecording = useCallback(
    (options: StopRecordingOptions = {}) => {
      stopVoiceRecordingSession(
        {
          recordingRef,
          micReleaseRef,
          micStreamRef,
          micRequestIdRef,
        },
        options,
      );
    },
    [micReleaseRef, micRequestIdRef, micStreamRef, recordingRef],
  );

  const releaseAudioInput = useCallback(() => {
    stopVoiceRecordingSession(
      {
        recordingRef,
        micReleaseRef,
        micStreamRef,
        micRequestIdRef,
      },
      { process: false, releaseInput: true },
    );
  }, [micReleaseRef, micRequestIdRef, micStreamRef, recordingRef]);

  const persistConversationId = useCallback(
    (id: string | null) => {
      setConversationId(id);
      if (id) safeLocalStorageSet(VOICE_CONVERSATION_STORAGE_KEY, id);
    },
    [setConversationId],
  );

  const analyzeSpeechForTilt = useCallback(async (_transcript: string) => {
    // PsychAssist ER is wearable and trading-activity based, not transcript based.
  }, []);

  const cancel = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    stopPlayback();
    stopRecording();
    busyRef.current = false;
    if (errorRecoveryRef.current) {
      clearTimeout(errorRecoveryRef.current);
      errorRecoveryRef.current = null;
    }
    setRuntimeState(enabledRef.current ? "listening" : "idle");
  }, [
    abortRef,
    busyRef,
    enabledRef,
    errorRecoveryRef,
    setRuntimeState,
    stopPlayback,
    stopRecording,
  ]);

  const sendText = useCallback(
    (
      text: string,
      mode: "chat" | "infraction" = "chat",
      options: VoiceSendOptions = {},
    ): Promise<VoiceSendResult | null> =>
      sendVoiceTextRequest({
        text,
        mode,
        options,
        backend,
        conversationId,
        permissionLevel,
        busyRef,
        abortRef,
        enabledRef,
        replyAudioEnabledRef,
        stopRecording,
        persistConversationId,
        setHasUserRequestActivity,
        setLastUserText,
        setLastAssistantText,
        setRuntimeState,
        setErrorState,
        analyzeSpeechForTilt,
        restartListening: () => startListeningRef.current(),
      }),
    [
      abortRef,
      analyzeSpeechForTilt,
      backend,
      busyRef,
      conversationId,
      enabledRef,
      permissionLevel,
      persistConversationId,
      replyAudioEnabledRef,
      setErrorState,
      setHasUserRequestActivity,
      setLastAssistantText,
      setLastUserText,
      setRuntimeState,
      startListeningRef,
      stopRecording,
    ],
  );

  const handleNativeTranscript = useCallback(
    (transcript: string) => {
      const prompt = transcript.trim();
      if (!prompt || busyRef.current || !enabledRef.current) return;

      stopRecording({ process: false });
      onTranscriptRef.current?.(prompt);
      setLastUserText(prompt);
      void sendText(prompt, "chat");
    },
    [
      busyRef,
      enabledRef,
      onTranscriptRef,
      sendText,
      setLastUserText,
      stopRecording,
    ],
  );

  const respondToInfraction = useCallback(
    async (input: InfractionPromptInput = {}) => {
      if (!enabledRef.current) return null;
      return sendText(buildInfractionPrompt(input), "infraction");
    },
    [enabledRef, sendText],
  );

  const processRecording = useCallback(
    (chunks: Blob[]) =>
      processVoiceRecordingFallback({
        chunks,
        backend,
        enabledRef,
        busyRef,
        setRuntimeState,
        setLastUserText,
        setErrorState,
        startListening: () => startListeningRef.current(),
        onTranscript: (transcript) => onTranscriptRef.current?.(transcript),
        sendText: (transcript) => void sendText(transcript, "chat"),
        localStt:
          typeof window !== "undefined" ? window.electron?.voiceStt : undefined,
      }),
    [
      backend,
      busyRef,
      enabledRef,
      onTranscriptRef,
      sendText,
      setErrorState,
      setLastUserText,
      setRuntimeState,
      startListeningRef,
    ],
  );

  const startListening = useCallback(() => {
    if (isVoiceWidgetSandboxPreview()) {
      if (!enabledRef.current || busyRef.current) return;
      setRuntimeState("listening");
      readySoundPlayedRef.current = true;
      return;
    }

    startVoiceListeningSession({
      enabledRef,
      busyRef,
      recordingRef,
      micReleaseRef,
      micStreamRef,
      micRequestIdRef,
      isSupported,
      permission,
      soundEnabled: isSoundEnabled && !readySoundPlayedRef.current,
      requestMic,
      stopRecording,
      processRecording,
      handleNativeTranscript,
      startListening: () => startListeningRef.current(),
      setRuntimeState,
      setErrorState,
      onReady: () => {
        readySoundPlayedRef.current = true;
      },
    });
  }, [
    busyRef,
    enabledRef,
    handleNativeTranscript,
    isSoundEnabled,
    isSupported,
    micReleaseRef,
    micRequestIdRef,
    permission,
    processRecording,
    readySoundPlayedRef,
    recordingRef,
    requestMic,
    setErrorState,
    setRuntimeState,
    startListeningRef,
    stopRecording,
  ]);

  return {
    cancel,
    isNativeSpeechRecognitionSupported,
    respondToInfraction,
    sendText,
    setErrorState,
    setReplyAudioEnabled,
    startListening,
    stopPlayback,
    stopRecording,
    releaseAudioInput,
  };
}
